"use client";

import Link from "next/link";

import { IS_PRODUCTION } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { markdownToSafeHTML } from "@calcom/lib/markdownToSafeHTML";
import { Button } from "@calcom/ui/components/button";
import { showToast } from "@calcom/ui/components/toast";

import type { AppDataProps } from "@lib/apps/[slug]/getStaticProps";
import useRouterQuery from "@lib/hooks/useRouterQuery";

import App from "@components/apps/App";

function AppSignupCTA({
  slug,
  iconPath,
  title,
  description,
  signupUrl,
  buttonLabel,
}: {
  slug: string;
  iconPath: string;
  title: string;
  description: string;
  signupUrl: string;
  buttonLabel: string;
}) {
  return (
    <div className="border-subtle bg-muted mb-6 rounded-lg border p-5">
      <div className="flex items-start gap-4">
        <img
          src={iconPath}
          alt={slug}
          className="h-12 w-12 rounded-md"
        />
        <div className="flex-1">
          <p className="text-emphasis text-sm font-semibold">
            {title}
          </p>
          <p className="text-default mt-1 text-sm">
            {description}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              color="primary"
              className="!no-underline"
              onClick={() => window.open(signupUrl, "_blank")}>
              {buttonLabel}
            </Button>
            <span className="text-subtle text-xs">
              After creating your account, come back here and click <strong>Connect</strong> to link it.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const APP_CTA_CONFIG: Record<string, { iconPath: string; title: string; description: string; signupUrl: string; buttonLabel: string }> = {
  supportly: {
    iconPath: "/app-store/supportly/icon.png",
    title: "Don\u2019t have a Supportly account?",
    description: "Supportly is an AI-powered customer support platform. Create a free account to start embedding booking links and managing customer meetings right inside your support conversations.",
    signupUrl: "https://app.supportly.io/app/auth/signup",
    buttonLabel: "Create Free Account",
  },
  zignature: {
    iconPath: "/app-store/zignature/icon.png",
    title: "Enable document signing for your business",
    description: "Collect e-signatures on consent forms, waivers, and contracts directly from your booking flow. Create a free account to get started.",
    signupUrl: "https://app.zignature.io/signup",
    buttonLabel: "Create Free Account",
  },
};

function SingleAppPage(props: AppDataProps) {
  const { error, setQuery: setError } = useRouterQuery("error");
  const { t } = useLocale();
  if (error === "account_already_linked" || error === "no_default_calendar") {
    showToast(t(error), "error", { id: error });
    setError(undefined);
  }
  if (props.isAppDisabled) {
    if (!IS_PRODUCTION) {
      return (
        <div className="p-2">
          This App seems to be disabled. If you are an admin, you can enable this app from{" "}
          <Link href="/settings/admin/apps" className="cursor-pointer text-blue-500 underline">
            here
          </Link>
        </div>
      );
    }

    return null;
  }

  const { source, data } = props;
  const ctaConfig = APP_CTA_CONFIG[data.slug];

  return (
    <App
      name={data.name}
      description={data.description}
      isGlobal={data.isGlobal}
      slug={data.slug}
      variant={data.variant}
      type={data.type}
      logo={data.logo}
      categories={data.categories ?? [data.category]}
      author={data.publisher}
      feeType={data.feeType || "usage-based"}
      price={data.price || 0}
      commission={data.commission || 0}
      docs={data.docsUrl}
      website={data.url}
      email={data.email}
      licenseRequired={data.licenseRequired}
      teamsPlanRequired={data.teamsPlanRequired}
      descriptionItems={source.data?.items as string[] | undefined}
      isTemplate={data.isTemplate}
      dependencies={data.dependencies}
      concurrentMeetings={data.concurrentMeetings}
      paid={data.paid}
      body={
        <>
          {ctaConfig && (
            <>
              <AppSignupCTA slug={data.slug} {...ctaConfig} />
              <hr className="border-subtle my-8 border" />
            </>
          )}
          {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via markdownToSafeHTML */}
          <div dangerouslySetInnerHTML={{ __html: markdownToSafeHTML(source.content) }} />
        </>
      }
    />
  );
}

export default SingleAppPage;
